import React, { useEffect, useMemo, useRef, useState } from 'react';
import LeaderboardItem from './LeaderboardItem';
import MobileAfterStage from './MobileAfterStage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import useStageResults from '../../../../useStageResults';
import ConnectionStatusIndicator from '../ConnectionStatusIndicator';
import { computeChangedRows, getRowKey, getStageRowValue } from '../../utils/highlightDiff';

const guestTextColors = [
  '#63AA57', '#6EB9E3', '#758AF8', '#8DEACE',
  '#B6F875', '#A2DDEA', '#9988FF', '#BECEF6', '#BCEAA0',
  '#ECA3A3', '#F5FF88', '#F875D3', '#FFBEA2', '#FFE5A2',
];

const createColorMapping = (data) => {
  const colorMapping = {};
  let colorIndex = 0;
  data.forEach((item) => {
    const inGroup = (item.inGroup || item.InGroup || '').trim();
    if (!inGroup || colorMapping[inGroup]) return;
    colorMapping[inGroup] = guestTextColors[colorIndex % guestTextColors.length];
    colorIndex++;
  });
  return colorMapping;
};

const StageLeaderboard = ({
  selectedEventID,
  selectedCompetitionID,
  selectedStageID,
  selectedClassificationID,
  selectedGroupID,
  stageStatus,
  stageStatusDesc,
  viewMode = 'odcinkowa',
}) => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [highlightedRows, setHighlightedRows] = useState(new Set());
  const prevResultsRef = useRef([]);
  const highlightTimerRef = useRef(null);

  const { stageResults, afterStageResults, loading, error } = useStageResults(
    selectedEventID,
    selectedCompetitionID,
    selectedStageID,
    selectedClassificationID,
    selectedGroupID
  );
  const hasResults = stageResults.length > 0 || afterStageResults.length > 0;

  useEffect(() => {
    const toggleScrollButton = () => {
      setShowScrollButton(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', toggleScrollButton);
    return () => window.removeEventListener('scroll', toggleScrollButton);
  }, []);

  useEffect(() => {
    const prev = prevResultsRef.current;
    if (prev.length === 0) {
      prevResultsRef.current = stageResults;
      return;
    }

    const newHighlights = computeChangedRows(prev, stageResults, getRowKey, getStageRowValue);

    if (newHighlights.size > 0) {
      clearTimeout(highlightTimerRef.current);
      setHighlightedRows(newHighlights);
      highlightTimerRef.current = setTimeout(() => setHighlightedRows(new Set()), 1800);
    }

    prevResultsRef.current = stageResults;
  }, [stageResults]);

  useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const colorMapping = useMemo(() => createColorMapping(stageResults), [stageResults]);

  if (loading) return <div className="loading-spinner"></div>;

  if (error) {
    return (
      <div className="notification has-text-centered mt-1">
        <strong>{error}</strong>
      </div>
    );
  }

  const mobileFinisherNumbers = new Set(
    stageResults
      .filter(r => (r.rowNumber ?? r.RowNumber) !== 'OUT')
      .map(r => r.positionNumber ?? r.PositionNumber)
  );

  const filteredAfterStageResults = hasResults
    ? afterStageResults.filter(r => mobileFinisherNumbers.has(r.positionNumber ?? r.PositionNumber) || (r.rowNumber ?? r.RowNumber) === 'OUT')
    : [];

  const header = (
    <div className="mobile-stage-header__wrapper">
      <div className="mobile-stage-header">
        <div className="mobile-stage-header__title">
          {viewMode === 'odcinkowa' ? 'Odcinkowa' : 'Po odcinku'}
        </div>
        <ConnectionStatusIndicator />
      </div>
    </div>
  );

  return (
    <div>
      {stageStatus ? (
        <div className="section">
          <div className="notification has-text-centered mt-1">
            <p>Odcinek odwołany {stageStatusDesc}</p>
          </div>
        </div>
      ) : (
        <div>
          {viewMode === 'odcinkowa' ? (
            <div key="odcinkowa" className="stage-view stage-view--odcinkowa">
              {header}
              {hasResults ? (
                <div
                  className="columns result-table is-bordered is-striped is-narrow is-hoverable is-fullwidth"
                  translate="no"
                >
                  <div>
                    {stageResults.map((data, index) => (
                      <LeaderboardItem
                        key={getRowKey(data) ?? index}
                        data={data}
                        colorMapping={colorMapping}
                        isHighlighted={highlightedRows.has(getRowKey(data))}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="notification has-text-centered mt-1">
                  <strong>Brak danych</strong>
                </div>
              )}
            </div>
          ) : (
            <div key="po-odcinku" className="stage-view stage-view--po-odcinku">
              {header}
              {!hasResults ? (
                <div className="notification has-text-centered mt-1">
                  <strong>Brak danych</strong>
                </div>
              ) : (
                <MobileAfterStage
                  results={filteredAfterStageResults}
                  loading={loading}
                  error={error}
                />
              )}
            </div>
          )}
          {showScrollButton && (
            <button onClick={scrollToTop} className="scroll-to-top-btn">
              <FontAwesomeIcon icon={faArrowUp} size="2x" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StageLeaderboard;
